import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import { fetchBatches } from '../../api/adminApi';
import { assignBatchToMentor } from '../../api/mentorApi';

const PAGE_SIZE = 20;

/**
 * Modal for assigning one or more batches to a mentor.
 *
 * Props:
 *   mentor          — mentor object (id, firstName, lastName, displayName)
 *   excludeBatchIds — Set of batch IDs already linked to this mentor (filtered out)
 *   onClose         — fired when the user cancels or closes
 *   onAssigned      — fired after assign(s) complete; receives { results, addedStudentCount, successCount, failedCount }
 */
const AssignBatchModal = ({ mentor, excludeBatchIds = new Set(), onClose, onAssigned }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Map<batchId, batch> so selections survive pagination/search changes
  const [selectedById, setSelectedById] = useState(new Map());
  const [submitting, setSubmitting] = useState(false);

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchBatches(page, PAGE_SIZE, search);
      setBatches(data.batches || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const toggleBatch = (batch) => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (next.has(batch.id)) {
        next.delete(batch.id);
      } else {
        next.set(batch.id, batch);
      }
      return next;
    });
  };

  const visibleBatches = batches.filter((b) => !excludeBatchIds.has(b.id));
  const selectableOnPage = visibleBatches;
  const allOnPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((b) => selectedById.has(b.id));
  const toggleSelectAllOnPage = () => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        selectableOnPage.forEach((b) => next.delete(b.id));
      } else {
        selectableOnPage.forEach((b) => next.set(b.id, b));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedById(new Map());

  const handleAssign = async () => {
    if (selectedById.size === 0) return;
    const selected = Array.from(selectedById.values());
    setSubmitting(true);
    try {
      // Assign sequentially: parallel calls all target the same mentor row and
      // race on the junction table, which caused some links to silently fail
      // and leave batches showing as partial.
      const successes = [];
      const failures = [];
      for (const b of selected) {
        try {
          const result = await assignBatchToMentor(mentor.id, b.id);
          successes.push({ batch: b, result });
        } catch (err) {
          failures.push({ batch: b, reason: err });
        }
      }
      const addedStudentCount = successes.reduce(
        (sum, s) => sum + (s.result?.addedStudentCount || 0),
        0
      );

      if (successes.length > 0) {
        toast.success(
          `Assigned ${successes.length} batch${successes.length === 1 ? '' : 'es'}` +
            (addedStudentCount > 0
              ? ` — linked ${addedStudentCount} student${addedStudentCount === 1 ? '' : 's'}.`
              : '.')
        );
      }
      if (failures.length > 0) {
        const first = failures[0].reason;
        const msg =
          first?.response?.data?.message ||
          first?.response?.data?.error ||
          `${failures.length} batch${failures.length === 1 ? '' : 'es'} failed to assign`;
        toast.error(msg);
      }

      onAssigned?.({
        results: successes,
        addedStudentCount,
        successCount: successes.length,
        failedCount: failures.length,
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to assign batches';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const mentorLabel =
    mentor?.displayName ||
    [mentor?.firstName, mentor?.lastName].filter(Boolean).join(' ') ||
    mentor?.username ||
    `Mentor #${mentor?.id}`;

  const selectedCount = selectedById.size;

  return (
    <div className="mentors-modal-overlay" onClick={onClose}>
      <div className="mentors-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mentors-modal-header">
          <h2 className="mentors-modal-title">
            <i
              className="bi bi-plus-circle"
              style={{ color: 'var(--accent-green)' }}
              aria-hidden="true"
            />
            Assign batches to{' '}
            <span className="mentors-modal-title-em">{mentorLabel}</span>
          </h2>
          <button
            className="mentors-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="mentors-modal-body">
          <div className="data-list-search-row" style={{ marginBottom: 12 }}>
            <div className="data-list-search-wrap" style={{ maxWidth: '100%' }}>
              <i className="bi bi-search data-list-search-icon" />
              <input
                type="text"
                className="data-list-search"
                placeholder="Search batches…"
                value={search}
                onChange={handleSearch}
                aria-label="Search batches"
              />
            </div>
          </div>

          <div className="mentors-modal-selection-bar">
            <span className="mentors-modal-selection-count">
              <i className="bi bi-check2-square" />{' '}
              {selectedCount} selected
            </span>
            <div className="mentors-modal-selection-actions">
              {selectableOnPage.length > 0 && (
                <button
                  type="button"
                  className="mentors-modal-pager-btn"
                  onClick={toggleSelectAllOnPage}
                  disabled={loading || submitting}
                >
                  <i className={`bi ${allOnPageSelected ? 'bi-square' : 'bi-check2-square'}`} />
                  {allOnPageSelected ? 'Unselect page' : 'Select page'}
                </button>
              )}
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="mentors-modal-pager-btn"
                  onClick={clearSelection}
                  disabled={submitting}
                >
                  <i className="bi bi-x-circle" /> Clear
                </button>
              )}
            </div>
          </div>

          <div className="mentors-modal-batch-list">
            {loading ? (
              <Skeleton count={5} height={44} borderRadius={10} />
            ) : visibleBatches.length === 0 ? (
              <div className="data-nested-empty">
                {search
                  ? 'No batches match your search.'
                  : 'All available batches are already linked to this mentor.'}
              </div>
            ) : (
              visibleBatches.map((batch) => {
                const isSelected = selectedById.has(batch.id);
                return (
                  <button
                    type="button"
                    key={batch.id}
                    className={`mentors-modal-batch-row${isSelected ? ' selected' : ''}`}
                    onClick={() => toggleBatch(batch)}
                    aria-pressed={isSelected}
                  >
                    <span className="mentors-modal-checkbox" aria-hidden="true">
                      <i
                        className={`bi ${isSelected ? 'bi-check-square-fill' : 'bi-square'}`}
                      />
                    </span>
                    <div className="mentors-modal-batch-main">
                      <span className="mentors-modal-batch-name">{batch.name}</span>
                      {batch.board && (
                        <span className="mentors-modal-batch-meta">{batch.board}</span>
                      )}
                    </div>
                    <span className="data-card-badge">
                      {batch._count?.students ?? 0} Students
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="mentors-modal-pager">
              <button
                type="button"
                className="mentors-modal-pager-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                <i className="bi bi-chevron-left" /> Prev
              </button>
              <span className="mentors-modal-pager-info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="mentors-modal-pager-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
              >
                Next <i className="bi bi-chevron-right" />
              </button>
            </div>
          )}
        </div>

        <div className="mentors-modal-footer">
          <button
            type="button"
            className="mentors-btn mentors-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="mentors-btn mentors-btn--primary"
            onClick={handleAssign}
            disabled={selectedCount === 0 || submitting}
          >
            {submitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: 6 }}
                />
                Assigning…
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle" style={{ marginRight: 6 }} />
                Assign {selectedCount > 0 ? `(${selectedCount})` : 'batches'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignBatchModal;
